import * as endpoints from "../configs/endpoints";
import * as types from "../constants/actionTypes";
import {
  DELETE_METHOD,
  GET_METHOD,
  POST_METHOD,
  PUT_METHOD,
} from "../constants/apiMethods";
import { callApi } from "../middleware/api";

export const getUsers = (
  page = 0,
  search = "",
  sort = "",
  filter = "",
  pageSize = 10
) => {
  return (dispatch, getState) => {
    const state = getState();
    const adminDetails = state?.auth?.adminDetails;

    if (adminDetails?.newBackendEnabled && adminDetails?.whiteLabelId) {
      return dispatch(getMergedUsers(page, search, sort, filter, pageSize));
    }

    return dispatch({
      endpoint: endpoints.getUsers(page, search, sort, filter, pageSize),
      method: GET_METHOD,
      types: [
        types.GET_USERS_REQUEST,
        types.GET_USERS_SUCCESS,
        types.GET_USERS_FAILURE,
      ],
    });
  };
};

const getMergedUsers = (page, search, sort, filter, pageSize) => {
  return async (dispatch, getState) => {
    const state = getState();
    const adminDetails = state?.auth?.adminDetails;
    const whiteLabelId = adminDetails?.whiteLabelId;

    let statusFilter = null;
    if (filter && filter.includes("userStatus=")) {
      const match = filter.match(/userStatus=([^&]+)/);
      if (match) statusFilter = match[1];
    }

    dispatch({ type: types.GET_USERS_REQUEST });

    try {
      const [newBackendResult, legacyResult] = await Promise.all([
        callApi(endpoints.getUsersFromNewBackend(), POST_METHOD, {
          legacy_whitelabel_id: String(whiteLabelId),
          search: search || null,
          status_filter: statusFilter,
          page,
          size: pageSize,
        }).catch(() => null),
        callApi(
          endpoints.getUsers(page, search, sort, filter, pageSize),
          GET_METHOD
        ).catch(() => null),
      ]);

      const newData = newBackendResult?.body ||
        newBackendResult || { content: [] };
      const legacyData = legacyResult || { content: [] };

      const usersById = new Map();

      const mergeUser = (existing, incoming) => {
        if (!existing) return { ...incoming };
        const merged = { ...existing };
        Object.keys(incoming).forEach((key) => {
          const incomingVal = incoming[key];
          const existingVal = existing[key];
          if (
            incomingVal !== null &&
            incomingVal !== undefined &&
            incomingVal !== "" &&
            incomingVal !== "-"
          ) {
            if (
              existingVal === null ||
              existingVal === undefined ||
              existingVal === "" ||
              existingVal === "-"
            ) {
              merged[key] = incomingVal;
            }
          }
        });
        return merged;
      };

      (legacyData.content || []).forEach((user) => {
        usersById.set(user.id, mergeUser(usersById.get(user.id), user));
      });

      (newData.content || []).forEach((user) => {
        usersById.set(user.id, mergeUser(usersById.get(user.id), user));
      });

      const mergedContent = Array.from(usersById.values());

      mergedContent.sort((a, b) => {
        const dateA = new Date(a.dateCreated || 0);
        const dateB = new Date(b.dateCreated || 0);
        return dateA - dateB;
      });

      const result = {
        content: mergedContent,
        totalElements: Math.max(
          newData.totalElements || 0,
          legacyData.totalElements || 0,
          mergedContent.length
        ),
        totalPages: Math.max(
          newData.totalPages || 1,
          legacyData.totalPages || 1
        ),
        size: pageSize,
        number: page,
      };

      dispatch({ type: types.GET_USERS_SUCCESS, result });
      return result;
    } catch (error) {
      dispatch({ type: types.GET_USERS_FAILURE });
      throw error;
    }
  };
};

export const getUser = (id) => {
  return (dispatch, getState) => {
    const state = getState();
    const adminDetails = state?.auth?.adminDetails;

    if (adminDetails?.newBackendEnabled) {
      return dispatch(getMergedUser(id));
    }

    return dispatch({
      endpoint: endpoints.getUser(id),
      method: GET_METHOD,
      types: [
        types.GET_USER_REQUEST,
        types.GET_USER_SUCCESS,
        types.GET_USER_FAILURE,
      ],
    });
  };
};

const getMergedUser = (id) => {
  return async (dispatch) => {
    dispatch({ type: types.GET_USER_REQUEST });

    const mergeUser = (a, b) => {
      if (!a) return b ? { ...b } : null;
      if (!b) return { ...a };
      const merged = { ...a };
      Object.keys(b).forEach((key) => {
        const bVal = b[key];
        const aVal = a[key];
        if (
          bVal !== null &&
          bVal !== undefined &&
          bVal !== "" &&
          bVal !== "-"
        ) {
          if (
            aVal === null ||
            aVal === undefined ||
            aVal === "" ||
            aVal === "-"
          ) {
            merged[key] = bVal;
          }
        }
      });
      return merged;
    };

    try {
      const [newBackendResult, legacyResult] = await Promise.all([
        callApi(endpoints.getUserFromNewBackend(), POST_METHOD, {
          user_id: parseInt(id, 10),
        }).catch(() => null),
        callApi(endpoints.getUser(id), GET_METHOD).catch(() => null),
      ]);

      const newUser =
        newBackendResult?.body?.user || newBackendResult?.user || null;
      const legacyUser = legacyResult?.content?.[0] || null;

      const user = mergeUser(legacyUser, newUser);

      if (!user) {
        dispatch({ type: types.GET_USER_FAILURE });
        return null;
      }

      const result = { content: [user] };
      dispatch({ type: types.GET_USER_SUCCESS, result });
      return result;
    } catch (error) {
      dispatch({ type: types.GET_USER_FAILURE });
      throw error;
    }
  };
};

export const getUserForNewPage = (id) => {
  return {
    endpoint: endpoints.getUser(id),
    method: GET_METHOD,
    types: [
      types.GET_USER_REQUEST,
      types.GET_USER_FOR_NEW_PAGE_SUCCESS,
      types.GET_USER_FAILURE,
    ],
  };
};

export const updateUser = (id, body) => {
  return {
    endpoint: endpoints.updateUser(id),
    method: PUT_METHOD,
    types: [
      types.UPDATE_USER_REQUEST,
      types.UPDATE_USER_SUCCESS,
      types.UPDATE_USER_FAILURE,
    ],
    body,
  };
};

export const getUserPlanStatuses = () => {
  return {
    endpoint: endpoints.userPlanStatuses(),
    method: GET_METHOD,
    types: [
      types.GET_PLAN_STATUSES_REQUEST,
      types.GET_PLAN_STATUSES_SUCCESS,
      types.GET_PLAN_STATUSES_FAILURE,
    ],
  };
};

export const getUserPlanTypes = () => {
  return {
    endpoint: endpoints.userPlanTypes(),
    method: GET_METHOD,
    types: [
      types.GET_PLAN_TYPES_REQUEST,
      types.GET_PLAN_TYPES_SUCCESS,
      types.GET_PLAN_TYPES_FAILURE,
    ],
  };
};

export const clearUserData = () => {
  return {
    type: types.CLEAR_USER_DATA,
  };
};

export const clearUser = () => {
  return {
    type: types.CLEAR_USER,
  };
};

export const removeUser = (id) => {
  return {
    endpoint: endpoints.removeUser(id),
    method: DELETE_METHOD,
    types: [
      types.DELETE_USERS_REQUEST,
      types.DELETE_USERS_SUCCESS,
      types.DELETE_USERS_FAILURE,
    ],
  };
};

export const cancelSubscription = (cityId, userId, planId, date) => {
  return {
    endpoint: endpoints.cancelSubscription(cityId, userId, planId, date),
    method: POST_METHOD,
    types: [
      types.CANCEL_SUBSCRIPTION_REQUEST,
      types.CANCEL_SUBSCRIPTION_SUCCESS,
      types.CANCEL_SUBSCRIPTION_FAILURE,
    ],
  };
};

export const getAllManagedUser = (
  page,
  search,
  selectedCity,
  sort,
  filter,
  size
) => {
  return {
    endpoint: endpoints.getAllManagedUser(
      page,
      search,
      selectedCity,
      sort,
      filter,
      size
    ),
    method: GET_METHOD,
    types: [
      types.GET_MANAGED_USER_LIST_REQUEST,
      types.GET_MANAGED_USER_LIST_SUCCESS,
      types.GET_MANAGED_USER_LIST_FAILURE,
    ],
  };
};

export const createInternalUser = (body) => {
  return {
    endpoint: endpoints.createInternalUser,
    method: POST_METHOD,
    types: [
      types.POST_CREATE_INTERNAL_USER_REQUEST,
      types.POST_CREATE_INTERNAL_USER_SUCCESS,
      types.POST_CREATE_INTERNAL_USER_FAILURE,
    ],
    body,
  };
};

export const getInternalUser = (email) => {
  return {
    endpoint: endpoints.getInternalUser(email),
    method: GET_METHOD,
    types: [
      types.GET_INTERNAL_USER_REQUEST,
      types.GET_INTERNAL_USER_SUCCESS,
      types.GET_INTERNAL_USER_FAILURE,
    ],
  };
};

export const updateInternalUser = (body, email) => {
  return {
    endpoint: endpoints.updateInternalUser(email),
    method: PUT_METHOD,
    types: [
      types.PUT_INTERNAL_USER_UPDATE_REQUEST,
      types.PUT_INTERNAL_USER_UPDATE_SUCCESS,
      types.PUT_INTERNAL_USER_UPDATE_FAILURE,
    ],
    body,
  };
};

export const deleteInternalUser = (email) => {
  return {
    endpoint: endpoints.deleteInternalUser(email),
    method: DELETE_METHOD,
    types: [
      types.DELETE_INTERNAL_USER_REQUEST,
      types.DELETE_INTERNAL_USER_SUCCESS,
      types.DELETE_INTERNAL_USER_FAILURE,
    ],
  };
};

export const clearInternalUserData = () => {
  return {
    type: types.CLEAR_INTERNAL_USER,
  };
};
